import { createCollection } from "@tanstack/react-db";
import { queryCollectionOptions } from "@tanstack/query-db-collection";
import type { QueryClient } from "@tanstack/react-query";

import type { BootstrapView, InboxFuryWebPort } from "~/api/ports";
import type { MailSearch } from "~/domain/search";
import type {
  ApiClientView,
  DomainResource,
  FolderView,
  Mailbox,
  Message,
  ScheduledSendView,
  ThreadSummary,
} from "~/domain/types";

/** Shared estate snapshot. Mailboxes, domains and API clients all read this key. */
export const BOOTSTRAP_QUERY_KEY = ["bootstrap"] as const;

/**
 * Parameterized collections (threads, messages, outbound, folders) are cached
 * by key so switching mailboxes does not remount a fetch engine. Capped so a
 * long filter-roaming session cannot grow the maps without bound.
 */
const PARAMETERIZED_COLLECTION_CAP = 32;

export interface AppCollections {
  mailboxes: ReturnType<typeof createMailboxCollection>;
  domains: ReturnType<typeof createDomainCollection>;
  apiClients: ReturnType<typeof createApiClientCollection>;
  threadsFor: (search: MailSearch) => ReturnType<typeof createThreadCollection>;
  messagesFor: (threadId: string) => ReturnType<typeof createMessageCollection>;
  outboundFor: (mailboxIds: readonly string[]) => ReturnType<typeof createOutboundCollection>;
  foldersFor: (mailboxId: string) => ReturnType<typeof createFolderCollection>;
}

/**
 * The one bootstrap read, shared by every collection built from it.
 *
 * The mailboxes collection used to call `port.bootstrap()` directly and seed
 * the cache itself, so it and this helper were two independent paths to the
 * same request. Refetching the three collections concurrently — which is what
 * a page load does — raced them, and the second path fired before the first
 * had written the cache. Every load paid for two `/bff/v1/bootstrap` round
 * trips, and two tests had been recording it as a failure for weeks.
 *
 * **Do not add a `staleTime` here.** It is deliberately absent so a sequential
 * refetch really re-reads: the Inbox badges are unread totals, and a cached
 * snapshot left them at their sign-in values after an archive or a read.
 * In-flight deduplication is what collapses the concurrent case; staleness is
 * what would break the freshness this relies on.
 */
function bootstrapSnapshot(queryClient: QueryClient, port: InboxFuryWebPort): Promise<BootstrapView> {
  return queryClient.fetchQuery({
    queryKey: BOOTSTRAP_QUERY_KEY,
    queryFn: () => port.bootstrap(),
  });
}

function createMailboxCollection(queryClient: QueryClient, port: InboxFuryWebPort) {
  return createCollection(
    queryCollectionOptions({
      id: "mailboxes",
      queryClient,
      queryKey: [...BOOTSTRAP_QUERY_KEY, "mailboxes"],
      queryFn: async () => (await bootstrapSnapshot(queryClient, port)).mailboxes,
      getKey: (row: Mailbox) => row.id,
      retry: false,
      enabled: false,
    }),
  );
}

function createDomainCollection(queryClient: QueryClient, port: InboxFuryWebPort) {
  return createCollection(
    queryCollectionOptions({
      id: "domains",
      queryClient,
      queryKey: [...BOOTSTRAP_QUERY_KEY, "domains"],
      queryFn: async () => (await bootstrapSnapshot(queryClient, port)).domains,
      getKey: (row: DomainResource) => row.id,
      retry: false,
      enabled: false,
    }),
  );
}

function createApiClientCollection(queryClient: QueryClient, port: InboxFuryWebPort) {
  return createCollection(
    queryCollectionOptions({
      id: "apiClients",
      queryClient,
      queryKey: [...BOOTSTRAP_QUERY_KEY, "apiClients"],
      queryFn: async () => (await bootstrapSnapshot(queryClient, port)).apiClients,
      getKey: (row: ApiClientView) => row.id,
      retry: false,
      enabled: false,
    }),
  );
}

function threadCacheKey(search: MailSearch): string {
  return JSON.stringify({
    folder: search.folder ?? "inbox",
    mailboxes: search.mailboxes ?? [],
    domains: search.domains ?? [],
    text: search.text,
    unread: Boolean(search.unread),
    starred: Boolean(search.starred),
    from: search.from ?? "",
    to: search.to ?? "",
    subject: search.subject ?? "",
    hasAttachment: Boolean(search.hasAttachment),
  });
}

function createThreadCollection(queryClient: QueryClient, port: InboxFuryWebPort, search: MailSearch) {
  const key = ["threads", threadCacheKey(search)];
  return createCollection(
    queryCollectionOptions({
      id: `threads:${key.join(":")}`,
      queryClient,
      queryKey: key,
      queryFn: async () => (await port.listThreads(search)).data,
      getKey: (row: ThreadSummary) => row.id,
      retry: false,
      enabled: false,
    }),
  );
}

function createMessageCollection(queryClient: QueryClient, port: InboxFuryWebPort, threadId: string) {
  return createCollection(
    queryCollectionOptions({
      id: `messages:${threadId}`,
      queryClient,
      queryKey: ["messages", threadId],
      queryFn: async () => (await port.getThread(threadId)).messages,
      getKey: (row: Message) => row.id,
      retry: false,
      enabled: false,
    }),
  );
}

function createOutboundCollection(
  queryClient: QueryClient,
  port: InboxFuryWebPort,
  mailboxIds: readonly string[],
) {
  const key = ["outbound", ...mailboxIds];
  return createCollection(
    queryCollectionOptions({
      id: `outbound:${mailboxIds.join(",")}`,
      queryClient,
      queryKey: key,
      queryFn: async () => {
        const pages = await Promise.all(mailboxIds.map((id) => port.listOutboundSends(id)));
        return pages.flatMap((page) => page.data);
      },
      getKey: (row: ScheduledSendView) => row.id,
      retry: false,
      enabled: false,
    }),
  );
}

function createFolderCollection(queryClient: QueryClient, port: InboxFuryWebPort, mailboxId: string) {
  return createCollection(
    queryCollectionOptions({
      id: `folders:${mailboxId}`,
      queryClient,
      queryKey: ["folders", mailboxId],
      queryFn: async () => port.listFolders(mailboxId),
      getKey: (row: FolderView) => row.id,
      retry: false,
      enabled: false,
    }),
  );
}

interface CachedCollection {
  subscriberCount: number;
  cleanup: () => Promise<void>;
}

function cachedCollection<V extends CachedCollection>(map: Map<string, V>, key: string, create: () => V): V {
  const existing = map.get(key);
  if (existing) {
    map.delete(key);
    map.set(key, existing);
    return existing;
  }
  const created = create();
  map.set(key, created);
  if (map.size <= PARAMETERIZED_COLLECTION_CAP) return created;
  for (const cachedKey of map.keys()) {
    if (map.size <= PARAMETERIZED_COLLECTION_CAP) break;
    if (cachedKey === key) continue;
    const candidate = map.get(cachedKey);
    if (!candidate || candidate.subscriberCount > 0) continue;
    map.delete(cachedKey);
    void candidate.cleanup();
  }
  return created;
}

/**
 * Client collections over the existing BFF. No second backend.
 *
 * Mailboxes, domains and API clients share one bootstrap query (same queryKey
 * root). Collections stay `enabled: false`; the hook or a screen calls
 * utils.refetch. Parameterized collections are cached by key so switching
 * mailboxes does not remount a fetch engine.
 */
export function createAppCollections(queryClient: QueryClient, port: InboxFuryWebPort): AppCollections {
  const threads = new Map<string, ReturnType<typeof createThreadCollection>>();
  const messages = new Map<string, ReturnType<typeof createMessageCollection>>();
  const outbound = new Map<string, ReturnType<typeof createOutboundCollection>>();
  const folders = new Map<string, ReturnType<typeof createFolderCollection>>();

  return {
    mailboxes: createMailboxCollection(queryClient, port),
    domains: createDomainCollection(queryClient, port),
    apiClients: createApiClientCollection(queryClient, port),
    threadsFor(search) {
      return cachedCollection(threads, threadCacheKey(search), () =>
        createThreadCollection(queryClient, port, search),
      );
    },
    messagesFor(threadId) {
      return cachedCollection(messages, threadId, () => createMessageCollection(queryClient, port, threadId));
    },
    outboundFor(mailboxIds) {
      return cachedCollection(outbound, mailboxIds.join(","), () =>
        createOutboundCollection(queryClient, port, mailboxIds),
      );
    },
    foldersFor(mailboxId) {
      return cachedCollection(folders, mailboxId, () => createFolderCollection(queryClient, port, mailboxId));
    },
  };
}
